const TRIGGER_SOURCE = 'ty-richtext-add-new-vehicle-to-wishlist';
const WISHLIST_ANALYTICS_CONSTANTS = Object.freeze({
  EVENT_NAME: "click_vehicle_add_to_wishlist",
  CTA_EVENT_NAME: "click_secondary_interaction",
  LINK_URL: null,
  CTA_CLICK_TEXT: 'Add to Wishlist',
  CLICK_TYPE: 'Icon',
  CTA_CLICK_TYPE: 'button',
  WISHLIST_ITEM_TYPE: 'new vehicles',
  MODULE_NAME: 'wishlist notifications',
  ELEMENT_NAME: null,
  DATE_FORMAT_OPTIONS: { year: "2-digit", month: "2-digit", day: "2-digit" },
  AUTHENTICATED: 'Authenticated',
  NOT_AUTHENTICATED: 'Unauthenticated'
});

const formatAnalyticsDate = (date) => date.toLocaleDateString("en-AU", WISHLIST_ANALYTICS_CONSTANTS.DATE_FORMAT_OPTIONS);

(function (angular) {
  "use strict";

  const ALLOW_ADD_RETRY_TIMEOUT_MS = 2000;

  angular.module("Toyota.Services").service("Wishlist", [
    "Range",
    "CustomEvents",
    "DebugLogger",
    "RangeImageApi",
    "UrlParser",
    "$analytics",
    function (Range, CustomEvents, DebugLogger, RangeImageApi, UrlParser, $analytics) {
      const WISHLIST_CUSTOM_EVENT_NAME = {
        addVehicleToWishlist: "tk:add-new-vehicle-to-wishlist",
        vehicleAddedToWishlist: "tk:add-new-vehicle-to-wishlist-success",
        vehicleFailedToAddToWishlist: "tk:add-new-vehicle-to-wishlist-failed",
        authenticationStateKnown: "tk:initial-authentication-state-known",
      };

      const wishlistCustomEvent = {
        addVehicleToWishlist: CustomEvents.createDispatcherAndListener(
          WISHLIST_CUSTOM_EVENT_NAME.addVehicleToWishlist
        ),
        vehicleAddedToWishlistSuccess: CustomEvents.createDispatcherAndListener(
          WISHLIST_CUSTOM_EVENT_NAME.vehicleAddedToWishlist
        ),
        authenticationStateKnown: CustomEvents.createDispatcherAndListener(
          WISHLIST_CUSTOM_EVENT_NAME.authenticationStateKnown
        ),
      };

      const exports = this;

      let authenticationState = null;

      const trackAnalyticsEvent = ({ model, transmissionGears, grade, materialCode, engineCapacity }, source) => {
        const isRichTextTriggerSource = source === TRIGGER_SOURCE;

        const eventData = {
          link_url: WISHLIST_ANALYTICS_CONSTANTS.LINK_URL,
          click_text: isRichTextTriggerSource ? WISHLIST_ANALYTICS_CONSTANTS.CTA_CLICK_TEXT : null,
          click_type: isRichTextTriggerSource  ? WISHLIST_ANALYTICS_CONSTANTS.CTA_CLICK_TYPE : WISHLIST_ANALYTICS_CONSTANTS.CLICK_TYPE,
          user_login: authenticationState?.isAuthenticated ? WISHLIST_ANALYTICS_CONSTANTS.AUTHENTICATED : WISHLIST_ANALYTICS_CONSTANTS.NOT_AUTHENTICATED,
          wishlist_item_type: WISHLIST_ANALYTICS_CONSTANTS.WISHLIST_ITEM_TYPE,
          vehicle_model: model,
          vehicle_transmission: transmissionGears,
          vehicle_grade: grade,
          vehicle_material_code: materialCode,
          vehicle_engine_capacity: engineCapacity,
          vehicle_saved_date: formatAnalyticsDate(new Date()),
          module_name: WISHLIST_ANALYTICS_CONSTANTS.MODULE_NAME,
          element_name: WISHLIST_ANALYTICS_CONSTANTS.ELEMENT_NAME
        }

        const eventName = isRichTextTriggerSource ? WISHLIST_ANALYTICS_CONSTANTS.CTA_EVENT_NAME : WISHLIST_ANALYTICS_CONSTANTS.EVENT_NAME;
        $analytics.eventTrack(eventName, eventData);
      };

      const getPricingPageInfoForMaterialCode = async function (materialCode) {
        const paint = await Range.getPaintByMaterialCode(materialCode);
        const variant = await Range.getVariantByMaterialCode(materialCode);
        const gradeId = await Range.getGradeIdByMaterialCode(materialCode);
        const grade = await Range.getGradeByIdFromCache(gradeId);
        const model = grade.vehicleModel;
        const imageUrl =
          (await RangeImageApi.getHeroByMaterialCode(materialCode)).images.find(
            (image) =>
              image.resolution === "519x292" && image.fileType === "webp"
          )?.url || null;

        return {
          model: model.Title,
          grade: grade.name,
          paintId: paint.ID,
          materialCode: materialCode,
          engineCapacity: variant.engineType,
          transmissionGears: variant.transmission,
          imageUrl: imageUrl,
        };
      };

      let pendingAddToWishlist = null;
      let lastAddedToWishlist = null;

      exports.clearPendingAddToWishlist = function () {
        pendingAddToWishlist = null;
      };

      exports.clearLastAddedToWishlist = function () {
        lastAddedToWishlist = null;
      };

      exports.addNewVehicleToWishlist = function (materialCode, source) {
        getPricingPageInfoForMaterialCode(materialCode).then(function (
          pricingPageInfo
        ) {
          const isAlreadyAdded =
            lastAddedToWishlist?.materialCode === materialCode;
          const isStillPendingAndWithinTimeout =
            pendingAddToWishlist?.materialCode === materialCode &&
            Date.now() - pendingAddToWishlist.timestamp <
              ALLOW_ADD_RETRY_TIMEOUT_MS;

          if (isAlreadyAdded || isStillPendingAndWithinTimeout) {
            const reasons = [];
            if (isAlreadyAdded) {
              reasons.push(`${materialCode} already added`);
            }

            if (isStillPendingAndWithinTimeout) {
              reasons.push(
                `${materialCode} is still pending and within timeout (${ALLOW_ADD_RETRY_TIMEOUT_MS}ms)`
              );
            }

            DebugLogger.log(
              "Wishlist",
              `Ignored addNewVehicleToWishlist call. Reason: ${reasons.join(
                "; "
              )}`
            );
            return;
          }

          const args = {
            returnUrl: UrlParser.overrideUrlQueryStringFromMap(
              window.location.href,
              { material_code: materialCode, add_to_wishlist: undefined }
            ),
            returnFromLoginFlowUrl: UrlParser.overrideUrlQueryStringFromMap(
              window.location.href,
              { material_code: materialCode, add_to_wishlist: 1 }
            ),
            restPayload: {
              vehicleDetails: pricingPageInfo,
            },
          };

          DebugLogger.log(
            "Wishlist",
            `Firing ${WISHLIST_CUSTOM_EVENT_NAME.addVehicleToWishlist} event with args:`,
            args,
            `Last added to wishlist was:`,
            lastAddedToWishlist
          );

          wishlistCustomEvent.addVehicleToWishlist.dispatch(args);
          pendingAddToWishlist = {
            timestamp: new Date(),
            materialCode: materialCode,
          };

          trackAnalyticsEvent(pricingPageInfo, source);
        });
      };

      exports.onAddNewVehicleToWishlistSuccess = function (listener) {
        return wishlistCustomEvent.vehicleAddedToWishlistSuccess.listener(
          listener
        );
      };

      exports.onAddNewVehicleToWishlistSuccess((e) => {
        exports.clearPendingAddToWishlist();
        lastAddedToWishlist = {
          materialCode: e.detail.materialCode,
          timestamp: new Date(),
        };
        DebugLogger.log(
          "Wishlist",
          `Received ${WISHLIST_CUSTOM_EVENT_NAME.vehicleAddedToWishlist} event with event data:`,
          e
        );
      });

      exports.onAuthenticationStateKnown = function (listener) {
        return wishlistCustomEvent.authenticationStateKnown.listener(listener);
      };

      wishlistCustomEvent.authenticationStateKnown.listener(function (e) {
        authenticationState = e.detail;
      });

      return exports;
    },
  ]);
})(window.angular);
